import React, { useMemo, useRef } from 'react';
import Input from '../Input';
import { Icon, Popover } from '@firesoon/antd';
import { useSize, useSetState, useDeepCompareEffect } from 'ahooks';
import { remove } from 'lodash';
import classNames from 'classnames';

import Tree from './Tree';
import './index.less';
import { MultipleSelectProps } from './interface';
import { addArr, editArr, includeArr, treeToArray } from './config';

const Index = ({
  label = '',
  hideLabel = false,
  tree = [],
  checkKeys = [],
  defaultExpandedKeys = [],
  showSearch = true,
  showCheckAll = false,
  width,
  dropProps,
  className = '',
  popupClassName = '',
  placeholder = '全部',
  searchPlaceholder = '请输入名称',
  logValue,
  onSelect,
}: MultipleSelectProps) => {
  const inputRef = useRef<any>();
  const searchRef: any = useRef(null);

  const size = useSize(inputRef);

  const [{ treeData, checkedList, allCheckedNodes, visible, inCheckKeys, touchChecked }, setState] = useSetState<any>({
    visible: false,
    checkedList: [],
    allCheckedNodes: [], // 所有被全选的父子节点关系集合
    inCheckKeys: [], // 选中的keys
    touchChecked: false,
    treeData: [],
  });

  // 展示选中的节点、格式化之后的回调传参
  const { showNodes, checkedChildren } = useMemo(() => {
    return getCheckedData(checkedList, allCheckedNodes);
  }, [checkedList, allCheckedNodes]);

  useDeepCompareEffect(() => {
    setState({
      inCheckKeys: checkKeys,
    });
  }, [checkKeys]);

  useDeepCompareEffect(() => {
    formatCheckedData({ tree, inCheckKeys });
  }, [tree, inCheckKeys]);

  useDeepCompareEffect(() => {
    loopSort(checkKeys);
  }, [tree]);

  const formatCheckedData = ({ tree, inCheckKeys }) => {
    const treeList = treeToArray(tree);
    const childItem = treeList.filter((item) => inCheckKeys?.includes(item.key));
    const all: any = [];

    tree.forEach((item) => {
      if (item.children?.length > 0) {
        const keys = item.children.map((child) => child.key);
        if (includeArr(inCheckKeys, keys)) {
          addArr(all, item);
          addArr(childItem, item);
        }
      }
    });

    setState({
      checkedList: childItem,
      allCheckedNodes: all,
    });
  };

  const clearSelect = (e) => {
    setState({
      checkedList: [],
      inCheckKeys: [],
    });
    onSelect?.([]);
  };

  const loopSort = (checked) => {
    const selectKeys = checked.map((i) => i.key ?? i);
    if (!selectKeys?.length) {
      setState({
        treeData: tree,
      });
      return;
    }
    const sort = (data) => {
      const selectedList = data.filter((item) => selectKeys.includes(item.key) || item.sorter);
      const notSelectedList = data.filter((item) => !selectKeys.includes(item.key) && !item.sorter);
      return [...selectedList, ...notSelectedList];
    };
    const loop = (data) => {
      data.forEach((item) => {
        if (item.children?.length) {
          if (item.children.filter((i) => selectKeys.includes(i.key))?.length) {
            item.children = sort(item.children);
            item.sorter = true;
          } else {
            item.sorter = false;
          }
          loop(item.children);
        }
      });
    };
    loop(tree);
    setState({
      treeData: [...sort(tree)],
    });
  };

  const handleSelect = (v) => {
    if (!v) {
      touchChecked && onSelect?.(checkedChildren);
      loopSort(checkedChildren);
    }
    searchRef?.current?.input?.setValue('');

    setState({
      visible: v,
      touchChecked: !v,
    });
  };

  function getCheckedData(checkedList, allCheckedNodes) {
    // 全选的父节点下的子节点合集
    let allCheckedChild: any = [];

    allCheckedNodes.forEach((node) => {
      if (node.children?.length > 0) {
        allCheckedChild = allCheckedChild.concat(node.children);
      }
    });
    // 格式化展示数据，全选只展示父级
    const showNodes = remove([...checkedList], (item: any) => {
      return !allCheckedChild.find((n) => n.key === item.key);
    });
    // 格式化选中结果，只保留子级
    const checkedChildren = remove([...checkedList], (item: any) => {
      return !allCheckedNodes.find((n) => n.key === item.key);
    });

    return {
      showNodes,
      checkedChildren,
    };
  }

  const onCheck = (_, info) => {
    const {
      checked,
      node: { props },
    } = info;
    const parentKeys = tree.filter((item) => item.children?.length > 0).map((item) => item.key);
    const { eventKey, parentKey } = props;
    const keys = [...inCheckKeys];

    if (!parentKeys.includes(eventKey)) {
      editArr(keys, eventKey, checked);

      if (!checked) {
        editArr(keys, parentKey, checked);
      }
    } else {
      const parentItem = tree.find((item) => item.key === eventKey);
      parentItem.children.forEach((p) => {
        editArr(keys, p.key, checked);
      });
    }

    setState({
      inCheckKeys: keys,
      touchChecked: true,
    });
  };

  const onCheckAll = (keys) => {
    setState({
      inCheckKeys: keys,
      touchChecked: true,
    });
  };

  const selectNames = useMemo(() => {
    return showNodes.map((item) => item.title);
  }, [showNodes]);

  const options = (
    <div className="fs-multiple-select-tree-opts-content">
      <Tree
        treeData={treeData}
        checkedList={checkedList}
        defaultExpandedKeys={defaultExpandedKeys}
        showSearch={showSearch}
        showCheckAll={showCheckAll}
        onCheckAll={onCheckAll}
        onCheck={onCheck}
        visible={visible}
        searchPlaceholder={searchPlaceholder}
        searchRef={searchRef}
        logValue={logValue}
      />
    </div>
  );

  return (
    <div className={classNames('fs-multiple-select-tree-wrap', className)}>
      {!hideLabel && label && (
        <label htmlFor="" className="fs-multiple-select-tree-wrap-label">
          {label}
        </label>
      )}

      <div className={classNames('fs-multiple-select-tree')} title={selectNames.join('，')}>
        <Popover
          content={options}
          trigger="click"
          overlayStyle={{ width: dropProps?.width ? dropProps?.width : size?.width ?? width }}
          overlayClassName={classNames('fs-multiple-select-tree-opts', popupClassName)}
          onVisibleChange={handleSelect}
          getPopupContainer={() => document.body}
          placement="bottomLeft"
        >
          <div ref={inputRef}>
            <Input
              allowClear
              className="fs-multiple-select-tree-show"
              disabled
              suffix={
                <Icon
                  type="down"
                  className={classNames({
                    'fs-icon-up': visible,
                    'fs-icon-down': !visible,
                    selected: !!selectNames.length,
                  })}
                />
              }
              value={selectNames.join('，')}
              placeholder={placeholder}
              style={{
                width,
              }}
            />
          </div>
        </Popover>

        {selectNames.length > 0 && (
          <Icon type="close-circle" theme="filled" className="fs-multiple-select-tree-clear" onClick={clearSelect} />
        )}
      </div>
    </div>
  );
};

export default Index;

export { Tree };
